<template>
  <v-card class="pb-0">

    <!-- Navigation Drawer -->
    <v-navigation-drawer
      v-if="isAdmin"
      v-model="drawer"
        app
        temporary
      >
      <v-list dense>
        <template v-for="(item, index) in menuItems">
          <template v-if="menuIsAuthorized(item)">
            <v-divider v-if="item.prefixWithDivider" :key="`divider-${index}`"></v-divider>
            <v-list-item :key="index" @click="menuAction(index)">
              <v-list-item-action>
                <v-icon :color="item.iconColor">{{ item.icon }}</v-icon>
              </v-list-item-action>
              <v-list-item-content>{{ getMenuText(item) }}</v-list-item-content>
            </v-list-item>
          </template>
        </template>

        <v-divider />

        <template v-if="authenticated">
          <v-list-item @click="signOut">
            <v-list-item-action>
              <v-icon>power_settings_new</v-icon>
            </v-list-item-action>
            <v-list-item-content>Sign Out</v-list-item-content>
          </v-list-item>
        </template>
        <template v-else>
          <v-list-item @click="signIn">
            <v-list-item-action>
              <v-icon>lock_open</v-icon>
            </v-list-item-action>
            <v-list-item-content>Sign Out</v-list-item-content>
          </v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>

    <!-- Appbar -->
    <v-app-bar app dark flat v-if="!hideToolbar" :color="appColor_Dark">
      <v-app-bar-nav-icon
        v-if="isAdmin"
        :disabled="disableDrawer"
        @click.stop="drawer = !drawer"
      />

      <span v-if="authenticated"
        @click="menuAction(0)"
        :class="toolbarTitleClass"
        :style="'cursor: pointer'">
        <span v-if="$vuetify.breakpoint.name !== 'xs'" >{{ getExpression('Label', 'Home') }}</span>
        <span v-else class="body-2">{{ getExpression('Label', 'Home') }}</span>
      </span>

      <!--<template v-if="authenticated">
        <v-toolbar-items v-if="!isAdmin" >
          <v-app-bar-nav-icon @click="menuAction(0)" class="ml-0">
            <v-img width="60" :src="imageUri_AmbientLogo" />
          </v-app-bar-nav-icon>
        </v-toolbar-items>
      </template>-->

      <v-spacer />
      
      <template v-if="authenticated">
        <span v-if="$vuetify.breakpoint.name !== 'xs'" class="text-uppercase subtitle-1 mr-3">{{ functionalRoleText }}</span>
        <span v-else class="text-uppercase body-2 mr-2">{{ functionalRoleText }}</span>
        <v-toolbar-items>
          <v-menu offset-y>
            <template v-slot:activator="{ on }">
              <v-btn :color="appColor_ExtraDark" v-on="on" class="elevation-0">
                <v-avatar size="50">
                  <img :src="profilePicture" />
                </v-avatar>
                <v-icon>arrow_drop_down</v-icon>
              </v-btn>
            </template>
            <v-list v-if="profile" dense>
              <v-list-item>
                <v-list-item-title class="font-weight-medium">{{ profile.name }}</v-list-item-title>
              </v-list-item>

              <v-divider />

              <v-list-item @click="editProfile">
                <v-list-item-avatar>
                  <v-icon :color="appColor_Medium">person</v-icon>
                </v-list-item-avatar>
                <v-list-item-title>
                  {{ getExpression('MenuItem', 'EditProfile')}} 
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="profilePasswordDialog = true" class="pb-0">
                <v-list-item-avatar>
                  <v-icon>security</v-icon>
                </v-list-item-avatar>
                <v-list-item-title>{{ getExpression('MenuItem', 'ChangePassword') }}</v-list-item-title>
              </v-list-item>

              <!-- temporary (for tammy) -->
              <v-list-item v-if="isTammy" @click="gotoUnupdatedAbbys" class="pb-0">
                <v-list-item-avatar>
                  <v-icon>settings</v-icon>
                </v-list-item-avatar>
                <v-list-item-title>Unupdated Abbys</v-list-item-title>
              </v-list-item>

              <v-divider />

              <v-list-item @click="signOut">
                <v-list-item-avatar>
                  <v-icon>power_settings_new</v-icon>
                </v-list-item-avatar>
                <v-list-item-title>{{ getExpression('MenuItem', 'SignOut') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-toolbar-items>
      </template>

      <template v-else>
        <v-toolbar-items >
          <v-btn
            :color="appColor_ExtraDark"
            @click="signIn"
            class="elevation-0"
            >Sign In
          </v-btn>
        </v-toolbar-items>
      </template>
    </v-app-bar>

    <!-- Profile Edit Dialog -->
    <v-layout row justify-center>
      <v-dialog
        v-model="profileEditDialog"
        max-width="330px"
        :persistent="true"
        >
        <profile-edit
          v-on:save="profileSaveHandler"
          v-on:cancel="profileEditDialog = false"
          v-on:error="errorHandler"
          v-bind:profile="profileModel"
          v-bind:profileOriginal="profileOriginal"
          v-bind:residents="profileResidents"
          v-bind:kiosks="profileKiosks"
          v-bind:languages="languages"
          v-bind:functionalRoleText="functionalRoleText"
          v-bind:functionalRoleColor="functionalRoleColor"
          >
        </profile-edit>
      </v-dialog>
    </v-layout>

    <!-- Profile Password Dialog -->
    <v-dialog
      v-model="profilePasswordDialog"
      max-width="300px"
      persistent
      >
      <profile-password
        v-bind:userId="profile.id"
        v-on:error="errorHandler"
        v-on:close="profilePasswordDialog = false"
      />
    </v-dialog>

  </v-card>
</template>

<script>
import appService from '@/app.service'
import ProfileEdit from '@/components/ProfileEdit'
import ProfilePassword from '@/components/ProfilePassword'
import { mapActions, mapGetters } from 'vuex'
import { AppColor, ImageUri, FunctionalUserRole, UserRole, UserIds } from '@/config.js'

export default {
  name: 'AppToolbar',
  components: {
    ProfileEdit,
    ProfilePassword
  },
  data: () => ({
    drawer: null,

    // colors
    appColor_Medium: AppColor.Medium,
    appColor_Dark: AppColor.Dark,
    appColor_ExtraDark: AppColor.ExtraDark,
    appColor_Genie: AppColor.KioskTypeGenie,
    appColor_Abby: AppColor.KioskTypeAbby,
    appColor_Resident: AppColor.Resident,
    appColor_Toolbar: AppColor.Toolbar,

    // roles
    role_Admin: UserRole.Admin,
    role_Guest: UserRole.Guest,
    role_Manager: UserRole.Manager,
    functionalRole_ResidentManager: FunctionalUserRole.ResidentManager,
    functionalRole_KioskManager: FunctionalUserRole.KioskManager,
    functionalRole_DomainManager: FunctionalUserRole.DomainManager,

    // image uri
    imageUri_NoAvatar: ImageUri.NoAvatar,
    imageUri_AmbientLogo: ImageUri.AmbientLogo,

    // edit profile dialog
    profilePasswordDialog: false,
    profileEditDialog: false,
    profileResidents: [],
    profileKiosks: [],
    profileModel: {
      id: null,
      email: null,
      name: null,
      nickname: null,
      picture: '',
      defaultResidentId: null
    },
    profileOriginal: {
      name: null,
      nickname: null,
      picture: '',
      defaultResidentId: null
    },
    // temporary (for tammy)
    user_Tammy: UserIds.Tammy
  }),
  computed: {
    ...mapGetters([
      'authenticated',
      'profile',
      'isAdmin',
      'languages',
      'currentRoute']),
    functionalRoleText () {
      if (this.isNullOrUndefined(this.profile.functionalRole)) return ''
      return this.getExpression('FunctionalRole', this.profile.functionalRole.replace(/ /g,''))
    },
    functionalRoleColor () {
      if (this.isNullOrUndefined(this.profile.functionalRoleColor)) return ''
      return !this.isAdmin
        ? this.profile.functionalRoleColor
        : '#fff'
    },
    menuItems () {
      var menuItems = []
      menuItems.push(
        {
          text: 'Home',
          name: 'Home',
          icon: 'home',
          iconColor: 'grey darken-1',
          prefixWithDivider: false,
          requiresAuth: false
        })
        menuItems.push({
          text: 'Organizations',
          name: 'OrganizationManager',
          icon: 'schema',
          iconColor: 'grey darken-1',
          prefixWithDivider: true,
          requiresAuth: true,
          isAdmin: true
        }) 
        menuItems.push({
          text: 'Domains',
          name: 'DomainManager',
          icon: 'domain',
          iconColor: 'grey darken-1',
          prefixWithDivider: false,
          requiresAuth: true,
          isAdmin: true
        })  
        menuItems.push({
          text: 'Facilities',
          name: 'FacilityManager',
          icon: 'location_city',
          iconColor: 'grey darken-1',
          prefixWithDivider: true,
          requiresAuth: true,
          isAdmin: true
        })        
        menuItems.push({
          text: 'Kiosks',
          name: 'KioskManager',
          icon: 'computer',
          iconColor: 'grey darken-1',
          prefixWithDivider: true,
          requiresAuth: true,
          isAdmin: true
        })
        menuItems.push({
          text: 'Kiosk Libraries',
          name: 'KioskMediaManager',
          icon: 'folder',
          iconColor: this.appColor_Genie,
          prefixWithDivider: false,
          requiresAuth: true,
          isKioskManager_Genie: true
        })
        menuItems.push({
          text: 'Community Playlists',
          name: 'KioskPlaylistManager',
          icon: 'perm_media',
          iconColor: this.appColor_Genie,
          prefixWithDivider: false,
          requiresAuth: true,
          isKioskManager_Genie: true
        })
        menuItems.push({
          text: 'Community Playlists',
          name: 'AbbyKioskProfileManager',
          icon: 'perm_media',
          iconColor: this.appColor_Abby,
          prefixWithDivider: false,
          requiresAuth: true,
          isKioskManager_Abby: true
        })
        menuItems.push({
          text: 'Residents',
          name: 'ResidentManager',
          icon: 'perm_identity',
          iconColor: 'grey darken-1',
          prefixWithDivider: true,
          requiresAuth: true,
          isManager: true
        })
        menuItems.push({
          text: 'Genie Libraries',
          name: 'ResidentMediaManager',
          icon: 'folder',
          iconColor: this.appColor_Resident,
          prefixWithDivider: false,
          requiresAuth: true,
          isResidentManager_Genie: true
        })
        menuItems.push({
          text: 'Genie Albums',
          name: 'ResidentPlaylistManager',
          icon: 'library_books',
          iconColor: this.appColor_Resident,
          prefixWithDivider: false,
          requiresAuth: true,
          isResidentManager_Genie: true
        })
        menuItems.push({
          text: 'Abby Playlists',
          name: 'AbbyResidentProfileManager',
          icon: 'perm_media',
          iconColor: this.appColor_Resident,
          prefixWithDivider: false,
          requiresAuth: true,
          isResidentManager_Abby: true
        })
        menuItems.push({
          text: 'Users',
          name: 'AdminUserManager',
          icon: 'people',
          prefixWithDivider: true,
          requiresAuth: true,
          isManager: true
        })
        menuItems.push({
          text: 'Messages',
          name: 'MessageManager',
          icon: 'mail',
          iconColor: this.appColor_Genie,
          prefixWithDivider: false,
          requiresAuth: true,
          isGenieManager: true
        })
        menuItems.push({
          text: 'Genie Public Media',
          name: 'PublicMediaManager',
          icon: 'folder_shared',
          iconColor: this.appColor_Genie,
          prefixWithDivider: true,
          requiresAuth: true,
          isAdmin: true
        })
        menuItems.push({
          text: 'Abby Shared Library',
          name: 'AbbySharedLibraryManager',
          icon: 'folder_shared',
          iconColor: this.appColor_Abby,
          prefixWithDivider: false,
          requiresAuth: true,
          isAdmin: true
        })
        // menuItems.push({
        //   text: 'Cira Stats',
        //   name: 'CiraStats',
        //   icon: 'library_books',
        //   iconColor: this.appColor_Resident,
        //   prefixWithDivider: true,
        //   requiresAuth: true,
        //   isAdmin: true
        // })

      return menuItems
    },
    profilePicture () {
      if (this.isNullOrUndefined(this.profile.picture)) return this.imageUri_NoAvatar

      return this.profile.picture.indexOf('https://s.gravatar.com/') >= 0
        ? this.imageUri_NoAvatar
        : this.profile.picture
    },
    hideToolbar () {
      return (this.$route.name === 'Logout')
    },
    disableDrawer () {
      if (this.$route.name === 'Home') return false
      return this.$route.meta.parentRoute !== '/'
    },
    isAdmin () {
      if (this.isNullOrUndefined(this.profile.role)) return false
      return this.profile.role === this.role_Admin
    },
    isGuest () {
      if (this.isNullOrUndefined(this.profile.role)) return false
      return this.profile.role === this.role_Guest
    },
    isTammy () {
      if (this.isNullOrUndefined(this.profile)) return false
      return this.user_Tammy.includes(this.profile.id)
    },
    roleClass () {
      return this.isSmallDevice
        ? 'mr-1 elevation-1 caption'
        : 'mr-4 elevation-1'
    },
    toolbarTitleClass () {
      // toolbar-title
      return this.isSmallDevice
        ? 'body-1 font-weight-regular text-uppercase ml-2'
        : 'subtitle-1 font-weight-regular text-uppercase ml-2'
    }
  },
  methods: {
    ...mapActions(['logout', 'initRouteStack']),

    signOut () {
      this.drawer = false
      this.menuAction(0)
      this.logout()
    },

    async signIn () {
      this.drawer = false
      await this.$auth.loginWithRedirect()
    },

    menuAction (index) {
      const name = this.menuItems[index].name
      this.drawer = false
      this.$store.dispatch('initRouteStack')
      if (name !== 'Home') {
        this.$store.dispatch('pushRoute', { name: name })
      }
      this.$router.push({ name: name }).catch(() => {})
    },

    menuIsAuthorized (menuItem) {
      if (this.profile.role === this.role_Admin)
        return true
      
      if (menuItem.isAdmin)
        return false

      if (menuItem.isGuest) 
        return this.profile.role === this.role_Guest

      // manager menu items
      if (menuItem.requiresAuth) {
        return this.authenticated
      }
      
      return this.authenticated      
    },

    loadProfileData () {
      return new Promise((resolve) => {
        const self = this
        appService.getForProfileEdit(self.profile).then(data => {
          resolve(data)
        }).catch(e => { self.handleCriticalError(e) })
      })
    },

    editProfile () {
      const self = this
      self.profileModel = {
        id: self.profile.id,
        email: self.profile.email,
        name: self.profile.name,
        nickname: self.profile.nickname,
        picture: self.profilePicture,
        defaultResidentId: self.profile.defaultResidentId,
        languageId: self.profile.languageId
      }
      self.profileOriginal = {
        name: self.profile.name,
        nickname: self.profile.nickname,
        picture: self.profilePicture,
        defaultResidentId: self.profile.defaultResidentId,
        languageId: self.profile.languageId
      }
      if (self.profileResidents.length === 0) {
        self.loadProfileData().then(data => {
          if (data.errorSeverity > 0) {
            self.showError(data.errorMessage)
          } else {
            self.profileResidents = data.residents
            self.profileKiosks = data.kiosks
            self.profileEditDialog = true
          }
        })
      } else {
        self.profileEditDialog = true
      }
    },

    profileSaveHandler () {
      this.profileEditDialog = false
      this.$store.dispatch('updateProfile', { profile: this.profileModel })
    },

    errorHandler (error) {
      this.profileEditDialog = false
      this.$error.show({
        title: this.getExpression('FormTitle', 'ProfileUpdateFailed'),
        text: error
      })
    },

    getMenuText(item) {
      if (!item.isAdmin && this.isAdmin) {
        return item.text.replace('Your', '')
      }
      return item.text
    },

    getFunctionalRoleCode (functionalRole) {
      if (functionalRole === this.functionalRole_ResidentManager)
        return 'FM'
      else if (functionalRole === this.functionalRole_KioskManager)
        return 'KM'
      else if (functionalRole === this.functionalRole_DomainManager)
        return 'DM'
      else if (functionalRole === this.functionalRole_Guest) return 'G'
      else if (functionalRole === this.functionalRole_Admin) return 'A'
      else return 'N/A'
    },

    // temporary (for tammy)
    gotoUnupdatedAbbys() {
      if (this.currentRoute !== 'Home') {
        this.initRouteStack()
      }
      this.$router.push({ name: 'UnupdatedAbbys' }).catch(() => {})
    }
  }
}
</script>

<style scoped>
  .toolbar-title {
    text-decoration: none !important;
    color: #fff;
  }
</style>